import { type ChatSession } from '@myre/app-agent-generated/types/ChatSession';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';

import { DatePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  signal,
  viewChild,
  type ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WORDINGS } from '../../../myre-wordings-loader';

import { ChatWindowService } from '../../chat-window.service';
import { type ChatActivityStatus } from '../../interfaces/chat.interfaces';
import { ChatSessionService } from '../../services/chat-session.service';

@Component({
  selector: 'myre-chat-footer-bar',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './chat-footer-bar.component.html',
  styleUrl: './chat-footer-bar.component.scss',
})
export class ChatFooterBarComponent {
  protected readonly _chatWindowService = inject(ChatWindowService);
  private readonly _chatSessionService = inject(ChatSessionService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  protected readonly _showHistory = signal(false);
  protected readonly _sessions = this._chatSessionService.sessions;
  protected readonly _sessionsLoading = this._chatSessionService.loading;

  // Id of the session currently being renamed inline (null when none).
  protected readonly _editingSessionId = signal<string | null>(null);
  protected readonly _editTitle = signal('');

  private readonly _renameInput =
    viewChild<ElementRef<HTMLInputElement>>('renameInput');

  protected _onTabClick(id: string): void {
    const window = this._chatWindowService.windows().find(w => w.id === id);

    if (window?.state === 'open') {
      this._chatWindowService.minimizeWindow(id);

      return;
    }

    this._chatWindowService.focusWindow(id);
  }

  protected _onTabClose(event: MouseEvent, id: string): void {
    event.stopPropagation();
    this._chatWindowService.closeWindow(id);
  }

  protected _onNewChat(): void {
    // If there's already an open empty window, close it (toggle behavior)
    const active = this._chatWindowService.activeWindow();

    if (active && !active.hasMessages) {
      this._chatWindowService.minimizeWindow(active.id);

      return;
    }

    this._chatWindowService.openNewChat();
  }

  protected _toggleHistory(): void {
    const opening = !this._showHistory();

    this._showHistory.set(opening);

    if (opening) {
      this._editingSessionId.set(null);
      this._chatSessionService.loadSessions();
    }
  }

  protected _onSelectSession(session: ChatSession): void {
    this._showHistory.set(false);
    this._chatWindowService.openSession(
      session.id,
      session.title || this.chatWordings.untitledSession(),
    );
  }

  protected _closeHistory(): void {
    this._showHistory.set(false);
  }

  protected _onRename(event: MouseEvent, session: ChatSession): void {
    event.stopPropagation();
    this._editingSessionId.set(session.id);
    this._editTitle.set(session.title ?? '');
    // The input renders only after this signal change, so focus on the next tick.
    setTimeout(() => this._renameInput()?.nativeElement.select());
  }

  protected _onRenameSubmit(session: ChatSession): void {
    if (this._editingSessionId() !== session.id) {
      return;
    }

    const title = this._editTitle().trim();

    this._editingSessionId.set(null);

    if (!title || title === session.title) {
      return;
    }

    this._chatSessionService
      .renameSession(session.id, title)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(updated => {
        const window = this._chatWindowService
          .windows()
          .find(w => w.initialSessionId === session.id);

        if (window) {
          this._chatWindowService.updateTitle(
            window.id,
            updated.title ?? title,
          );
        }
      });
  }

  protected _onRenameCancel(): void {
    this._editingSessionId.set(null);
  }

  protected _onDelete(event: MouseEvent, session: ChatSession): void {
    event.stopPropagation();

    const window = this._chatWindowService
      .windows()
      .find(w => w.initialSessionId === session.id);

    this._chatSessionService.deleteSession(session.id);

    if (window) {
      this._chatWindowService.closeWindow(window.id);
    }
  }

  protected _hintLabel(status: ChatActivityStatus): string {
    if (status === 'processing') {
      return this.chatWordings.thinking();
    }

    if (status === 'question') {
      return this.chatWordings.awaitingAnswer();
    }

    return this.chatWordings.newReply();
  }
}
